import React, { useState, useEffect } from 'react';
import { ArrowLeft, MessageSquare, Mail, Calendar, RefreshCw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface Suggestion {
  id: string;
  name: string;
  email: string;
  suggestion: string;
  timestamp: string;
  read: boolean;
}

export function AdminSuggestionsPage() {
  const navigate = useNavigate();
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedSuggestion, setSelectedSuggestion] = useState<Suggestion | null>(null);

  const loadSuggestions = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch('/api/admin/suggestions', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('whipt_session_token')}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to load suggestions');
      }

      const data = await response.json();
      setSuggestions(data.suggestions || []);
    } catch (err) {
      console.error('Error loading suggestions:', err);
      setError('Failed to load suggestions. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSuggestions();
  }, []);

  const markAsRead = async (id: string) => {
    try {
      await fetch(`/api/admin/suggestions/${id}/read`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('whipt_session_token')}`,
        },
      });

      setSuggestions(prev => 
        prev.map(s => s.id === id ? { ...s, read: true } : s)
      );
    } catch (err) {
      console.error('Error marking as read:', err);
    }
  };

  const deleteSuggestion = async (id: string) => {
    if (!confirm('Are you sure you want to delete this suggestion?')) {
      return;
    }

    try {
      await fetch(`/api/admin/suggestions/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('whipt_session_token')}`,
        },
      });

      setSuggestions(prev => prev.filter(s => s.id !== id));
      setSelectedSuggestion(null);
    } catch (err) {
      console.error('Error deleting suggestion:', err);
    }
  };

  const unreadCount = suggestions.filter(s => !s.read).length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-6 py-4 shadow-lg">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/admin')}
              className="text-white hover:text-amber-100 transition-colors"
            >
              <ArrowLeft className="w-6 h-6" />
            </button>
            <div className="flex items-center gap-3">
              <MessageSquare className="w-8 h-8 text-white" />
              <div>
                <h1 className="text-2xl font-bold text-white">Suggestions</h1>
                <p className="text-sm text-amber-100">
                  {unreadCount} unread • {suggestions.length} total
                </p>
              </div>
            </div>
          </div>
          
          <button
            onClick={loadSuggestions}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-white/20 hover:bg-white/30 text-white rounded-lg transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6">
        {error && (
          <div className="bg-red-500/20 border border-red-500/50 rounded-xl p-4 mb-6 text-red-300">
            {error}
          </div>
        )}

        {loading ? (
          <div className="text-center py-12">
            <div className="w-12 h-12 border-4 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-slate-400">Loading suggestions...</p>
          </div>
        ) : suggestions.length === 0 ? (
          <div className="text-center py-12">
            <MessageSquare className="w-16 h-16 text-slate-600 mx-auto mb-4" />
            <p className="text-slate-400 text-lg">No suggestions yet</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-3 gap-6">
            {/* Suggestions List */}
            <div className="md:col-span-1 space-y-3">
              {suggestions.map((suggestion) => (
                <div
                  key={suggestion.id}
                  onClick={() => {
                    setSelectedSuggestion(suggestion);
                    if (!suggestion.read) {
                      markAsRead(suggestion.id);
                    }
                  }}
                  className={`p-4 rounded-xl cursor-pointer transition-all ${
                    selectedSuggestion?.id === suggestion.id
                      ? 'bg-amber-500/20 border-2 border-amber-500'
                      : suggestion.read
                      ? 'bg-slate-800/50 border border-slate-700 hover:bg-slate-800'
                      : 'bg-amber-500/10 border-2 border-amber-500/50 hover:bg-amber-500/15'
                  }`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="font-bold text-white">{suggestion.name}</h3>
                    {!suggestion.read && (
                      <span className="w-2 h-2 bg-amber-500 rounded-full" />
                    )}
                  </div>
                  <p className="text-sm text-slate-400 mb-2 flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    {suggestion.email}
                  </p>
                  <p className="text-sm text-slate-400 flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    {new Date(suggestion.timestamp).toLocaleString()}
                  </p>
                  <p className="text-sm text-slate-300 mt-2 line-clamp-2">
                    {suggestion.suggestion}
                  </p>
                </div>
              ))}
            </div>

            {/* Suggestion Detail */}
            <div className="md:col-span-2">
              {selectedSuggestion ? (
                <div className="bg-slate-800 rounded-xl border border-slate-700 p-6">
                  <div className="flex items-start justify-between mb-6">
                    <div>
                      <h2 className="text-2xl font-bold text-white mb-2">
                        {selectedSuggestion.name}
                      </h2>
                      <a
                        href={`mailto:${selectedSuggestion.email}`}
                        className="text-amber-400 hover:text-amber-300 flex items-center gap-2"
                      >
                        <Mail className="w-4 h-4" />
                        {selectedSuggestion.email}
                      </a>
                      <p className="text-slate-400 text-sm mt-2 flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        {new Date(selectedSuggestion.timestamp).toLocaleString()}
                      </p>
                    </div>
                    
                    <button
                      onClick={() => deleteSuggestion(selectedSuggestion.id)}
                      className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors"
                    >
                      Delete
                    </button>
                  </div>

                  <div className="bg-slate-900/50 rounded-lg p-6 border border-slate-700">
                    <h3 className="text-lg font-bold text-white mb-3">Suggestion:</h3>
                    <p className="text-slate-300 whitespace-pre-wrap leading-relaxed">
                      {selectedSuggestion.suggestion}
                    </p>
                  </div>

                  <div className="mt-6 flex gap-3">
                    <a
                      href={`mailto:${selectedSuggestion.email}?subject=Re: Your WHIPT Suggestion`}
                      className="flex-1 px-6 py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white font-bold rounded-xl hover:shadow-lg hover:shadow-amber-500/50 transition-all text-center"
                    >
                      Reply via Email
                    </a>
                  </div>
                </div>
              ) : (
                <div className="bg-slate-800 rounded-xl border border-slate-700 p-12 text-center">
                  <MessageSquare className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                  <p className="text-slate-400">Select a suggestion to view details</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
